import { useState } from "react";
import { Sparkles, ArrowLeft, Check, TrendingUp, Target, Lock, Share2 } from "lucide-react";
import { toast } from "sonner";
import {
  getVentaHoy,
  saveVentaHoy,
  getLast7Days,
  getMonthTotal,
  getBestDayThisMonth,
  getMeta,
  saveMeta,
  getRatingHoy,
  saveRatingHoy,
} from "../lib/ventas";

const pesos = (n) => `$${Math.round(n).toLocaleString("es-MX")}`;

const RATING_OPTIONS = [
  { id: "excelente", label: "Excelente", emoji: "🤩" },
  { id: "bien", label: "Bien", emoji: "🙂" },
  { id: "regular", label: "Regular", emoji: "😕" },
];

export default function MisVentas({ onBack }) {
  const [input, setInput] = useState(() => {
    const v = getVentaHoy();
    return v != null ? String(v) : "";
  });
  const [rating, setRating] = useState(() => getRatingHoy());
  const [meta, setMetaInput] = useState(() => {
    const m = getMeta();
    return m > 0 ? String(m) : "";
  });
  const [tick, setTick] = useState(0); // bump to recompute derived data after save

  const metaValue = Number(meta) > 0 ? Number(meta) : 0;
  const days = getLast7Days();
  const monthTotal = getMonthTotal();
  const bestDay = getBestDayThisMonth();
  const maxAmount = Math.max(...days.map((d) => d.amount), 1);
  const todayKeyRef = days[days.length - 1].key;

  // Weekly numeric summary (only when there's at least one exact amount logged).
  const numericDays = days.filter((d) => d.amount > 0);
  const hasNumeric = numericDays.length > 0;
  const bestWeekDay = hasNumeric
    ? numericDays.reduce((a, b) => (b.amount > a.amount ? b : a))
    : null;
  const weekAvg = hasNumeric
    ? Math.round(numericDays.reduce((s, d) => s + d.amount, 0) / numericDays.length)
    : 0;

  // Ritmo line: shows when there are 3+ numeric sales records stored (all time).
  const ritmo = (() => {
    let all = {};
    try {
      all = JSON.parse(localStorage.getItem("negoia:ventas") || "{}");
    } catch {
      all = {};
    }
    const entries = Object.entries(all).filter(([, v]) => typeof v === "number");
    if (entries.length < 3) return null;
    const DAY_NAMES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
    const best = entries.reduce((a, b) => (b[1] > a[1] ? b : a));
    return DAY_NAMES[new Date(`${best[0]}T00:00:00`).getDay()];
  })();

  const handleShareWeek = () => {
    const config = {
      type: "bar",
      data: {
        labels: days.map((d) => d.label),
        datasets: [
          {
            label: "Ventas ($)",
            data: days.map((d) => d.amount),
            backgroundColor: "#14b8a6",
          },
        ],
      },
      options: {
        title: { display: true, text: "Mi semana de ventas — NegoIA" },
        legend: { display: false },
      },
    };
    const chartUrl = `https://quickchart.io/chart?w=500&h=300&c=${encodeURIComponent(
      JSON.stringify(config),
    )}`;
    const msg = `¡Así me fue esta semana con mi negocio! 📈 Impulsa el tuyo gratis en negoia.mx\n${chartUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  const handleMetaChange = (val) => {
    setMetaInput(val);
    const n = Number(val);
    saveMeta(Number.isFinite(n) && n > 0 ? n : 0);
  };

  const handleSave = () => {
    const hasAmount = input.trim() !== "";
    const amount = Number(input);
    if (!rating && !hasAmount) {
      toast.error("Elige cómo te fue hoy 🙂");
      return;
    }
    if (hasAmount && (Number.isNaN(amount) || amount < 0)) {
      toast.error("El monto debe ser un número válido.");
      return;
    }
    if (rating) saveRatingHoy(rating);
    if (hasAmount) saveVentaHoy(amount);
    setTick((t) => t + 1);
    if (hasAmount && metaValue > 0 && amount >= metaValue) {
      toast.success("¡Meta alcanzada! 🎯🎉", { duration: 1800 });
    } else {
      toast.success("¡Guardado! 📊", { duration: 1500 });
    }
  };

  return (
    <div
      key={tick}
      className="flex flex-col min-h-[100dvh] px-6 pt-10 pb-10 animate-fade-in-up"
      data-testid="mis-ventas-screen"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-orange-500 text-white shadow-md shadow-orange-500/30">
            <Sparkles className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <span className="text-lg font-bold tracking-tight text-slate-900">
            Nego<span className="text-orange-500">IA</span>
          </span>
        </div>
        <button
          type="button"
          onClick={onBack}
          data-testid="mis-ventas-back-button"
          className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 active:text-slate-800"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.4} />
          Volver
        </button>
      </div>

      {/* Title */}
      <div className="mt-6 flex items-start gap-3">
        <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-teal-600 text-white shrink-0 shadow-md shadow-teal-600/30">
          <TrendingUp className="w-6 h-6" strokeWidth={2.5} />
        </span>
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 leading-tight">
            📊 Mis Ventas
          </h1>
          <p className="text-xs text-slate-500 mt-1 leading-snug">
            Anota tus ventas del día y mira tu semana.
          </p>
        </div>
      </div>

      {/* Today's check-in */}
      <div className="mt-8">
        <p className="block text-sm font-semibold text-slate-800 mb-2">
          ¿Cómo te fue hoy?
        </p>
        <div className="grid grid-cols-3 gap-2" data-testid="rating-options">
          {RATING_OPTIONS.map((opt) => {
            const selected = rating === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setRating(opt.id)}
                data-testid={`rating-option-${opt.id}`}
                className={[
                  "flex flex-col items-center justify-center gap-1 py-3 rounded-xl border-2 font-bold text-sm transition-all active:scale-[0.97]",
                  selected
                    ? "border-teal-600 bg-teal-50 text-teal-800"
                    : "border-slate-200 bg-slate-50 text-slate-600",
                ].join(" ")}
              >
                <span className="text-2xl leading-none">{opt.emoji}</span>
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Optional exact amount */}
        <label
          htmlFor="venta-hoy"
          className="block text-sm font-semibold text-slate-800 mb-2 mt-5"
        >
          Monto exacto <span className="text-slate-400 font-medium">(opcional, en pesos)</span>
        </label>
        <div className="flex items-stretch gap-2">
          <div className="relative flex-1">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
            <input
              id="venta-hoy"
              type="number"
              inputMode="numeric"
              min="0"
              step="1"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="0"
              data-testid="venta-hoy-input"
              className="w-full pl-8 pr-4 py-4 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-900 text-lg font-bold focus:border-teal-600 focus:bg-white outline-none transition-all placeholder:text-slate-300"
            />
          </div>
          <button
            type="button"
            onClick={handleSave}
            data-testid="venta-hoy-save-button"
            className="shrink-0 inline-flex items-center justify-center gap-1.5 px-5 rounded-xl text-base font-bold bg-orange-500 text-white shadow-lg shadow-orange-500/30 active:bg-orange-600 active:scale-[0.98] transition-all"
          >
            <Check className="w-5 h-5" strokeWidth={2.6} />
            Guardar
          </button>
        </div>

        {/* Privacy note */}
        <p
          className="flex items-start gap-1.5 text-xs text-slate-500 mt-2.5 leading-snug"
          data-testid="ventas-privacy-note"
        >
          <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" strokeWidth={2.4} />
          🔒 Solo tú ves esto. Tu información es completamente privada.
        </p>
      </div>

      {/* Daily goal */}
      <div className="mt-5">
        <label
          htmlFor="meta-dia"
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 mb-2"
        >
          <Target className="w-4 h-4 text-teal-700" strokeWidth={2.2} />
          Meta del día{" "}
          <span className="text-slate-400 font-medium">(opcional)</span>
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
          <input
            id="meta-dia"
            type="number"
            inputMode="numeric"
            min="0"
            step="1"
            value={meta}
            onChange={(e) => handleMetaChange(e.target.value)}
            placeholder="Ej: 1500"
            data-testid="meta-dia-input"
            className="w-full pl-8 pr-4 py-3.5 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-900 text-base font-bold focus:border-teal-600 focus:bg-white outline-none transition-all placeholder:text-slate-300"
          />
        </div>
        {metaValue > 0 && (
          <p className="text-xs text-slate-500 mt-1.5" data-testid="meta-dia-hint">
            🎯 Las barras se ponen verdes cuando alcanzas tu meta.
          </p>
        )}
      </div>

      {/* Bar chart — last 7 days */}
      <div className="mt-10" data-testid="ventas-chart">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
          Últimos 7 días
        </p>
        <div className="flex items-end justify-between gap-2 h-40">
          {days.map((d) => {
            const heightPct = d.amount > 0 ? Math.max((d.amount / maxAmount) * 100, 6) : 2;
            const isToday = d.key === todayKeyRef;
            const reachedGoal = metaValue > 0 && d.amount >= metaValue;
            const barColor = reachedGoal
              ? "bg-green-500"
              : isToday
                ? "bg-orange-500"
                : d.amount > 0
                  ? "bg-teal-500"
                  : "bg-slate-200";
            return (
              <div
                key={d.key}
                className="flex-1 flex flex-col items-center justify-end h-full"
                data-testid={`ventas-bar-${d.key}`}
              >
                {d.amount > 0 && (
                  <span className="text-[10px] font-bold text-slate-600 mb-1 leading-none">
                    {pesos(d.amount)}
                  </span>
                )}
                <div
                  className={["w-full rounded-t-lg transition-all duration-300", barColor].join(" ")}
                  style={{ height: `${heightPct}%` }}
                />
                <span
                  className={[
                    "text-[11px] mt-1.5 leading-none",
                    isToday ? "font-bold text-orange-600" : "font-semibold text-slate-500",
                  ].join(" ")}
                >
                  {d.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Ritmo — una línea condicional (3+ registros numéricos) */}
      {ritmo && (
        <p
          className="mt-3 text-xs text-slate-500 leading-snug"
          data-testid="ventas-ritmo-line"
        >
          Tu mejor día esta semana: {ritmo}. Sigue publicando para mantener el ritmo.
        </p>
      )}

      {/* Resumen semanal + compartir (solo si hay montos numéricos) */}
      {hasNumeric && (
        <>
          <p
            className="mt-4 text-sm text-slate-700 leading-relaxed"
            data-testid="ventas-week-summary"
          >
            Tu mejor día esta semana:{" "}
            <span className="font-bold text-slate-900">{bestWeekDay.label}</span> con{" "}
            <span className="font-bold text-teal-700">{pesos(bestWeekDay.amount)}</span>. Tu
            promedio diario:{" "}
            <span className="font-bold text-slate-900">{pesos(weekAvg)}</span>.
          </p>

          <button
            type="button"
            onClick={handleShareWeek}
            data-testid="share-week-button"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold bg-teal-600 text-white active:bg-teal-700 active:scale-[0.98] transition-all"
          >
            <Share2 className="w-4 h-4" strokeWidth={2.4} />
            📊 Compartir mi semana
          </button>
        </>
      )}

      {/* Month total */}
      <div
        className="mt-8 rounded-2xl p-5 bg-slate-900 text-white"
        data-testid="ventas-month-total"
      >
        <p className="text-xs font-bold uppercase tracking-wider text-teal-400">
          Total del mes
        </p>
        <p className="text-4xl font-extrabold mt-1" data-testid="ventas-month-total-value">
          {pesos(monthTotal)}
        </p>
      </div>

      {/* Best day */}
      <p
        className="mt-4 text-sm font-bold text-slate-800"
        data-testid="ventas-best-day"
      >
        🏆 Tu mejor día este mes:{" "}
        <span className="text-orange-500">{pesos(bestDay)}</span>
      </p>
    </div>
  );
}
